import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { Row, Col } from 'react-bootstrap'
import NoImage from '../../Assets/Images/Logos/no-image.png'
import { Plus, Dash } from 'react-bootstrap-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSquarePen, faTrashCan } from '@fortawesome/free-solid-svg-icons'
import PrecioProducto from '../PrecioProducto'
import GenericModal from '../GenericModal'
import Modal from 'react-bootstrap/Modal'
import InputMask from 'react-input-mask'
import { useDispatch, useSelector } from 'react-redux'
import { updateCartItem } from '../../redux/actions/cartActions'
import { Formatter } from 'Utils/Formatter'
import './cartProduct.css'
import { t } from 'i18next'
import { useProductQuantity } from './hooks/useProductQuantity'
import useValidatePermission from 'Hooks/useValidatePermission'
import { VAPermissions, noWarranty } from 'Utils/constants'
import { isDummyLitaProduct } from 'Utils/functions'
import CostMarginVisibility from 'Components/CostMarginVisibility/CostMarginVisibility'
import ServiceCard from 'Components/ServiceCard/ServiceCard'
import { useProductDetails } from 'Hooks/Products/useProductDetails'
import { useTintsController } from 'Hooks/useTintsController'
import TintDropdown from 'Hooks/useTintsController/TintDropdown'
import ExtendedWarrantyCard from 'Components/ExtendedWarrantyCard/ExtendedWarrantyCard'
import useHandleWarranty from 'Hooks/Cart/useHandleWarranty'
import { getProductDisplayText } from 'Utils/getProductDisplayText'
import { useWarrantyTypes } from 'Hooks/Products/useWarrantyTypes'

function CartProduct({
  product,
  productsRef,
  index,
  onPriceChangeValidation,
  onDeleteFromCart,
  hasPriceUnderCost,
  isCommercialSeller,
  disableDeleteProduct = false,
}) {
  const isWarrantyItem =
    !!product?.IsExtendedWarranty || product?.WarrantyProductId != null

  const {
    handleProdQuantity,
    quantity,
    allowFractionalQty,
    handleAddProduct,
    handleRemoveProduct,
    handleBlur,
  } = useProductQuantity(product)

  const setProductChangeValidation = useCallback(
    value => onPriceChangeValidation(index, value),
    [index, onPriceChangeValidation]
  )

  // Redux selectors
  const dispatch = useDispatch()
  const isCustomerUser = useSelector(state => state.login.isCustomer)
  const warehouse = useSelector(state => state.company.WarehouseId)
  const companyId = useSelector(state => state.company.CompanyId)
  const warrantyCartItem = useSelector(state =>
    state.cart.cart.find(
      item =>
        item.IsExtendedWarranty && item.WarrantyProductId === product.ProductId
    )
  )

  const [installable, setInstallable] = useState(product?.installable)
  const [hasExtendedWarranty, setHasExtendedWarranty] = useState(
    product?.hasExtendedWarranty || false
  )
  const [relatedServices, setRelativeServices] = useState(
    product.relatedServices || []
  )
  // variants
  const [hasVariant, setHasVariant] = useState(product?.HasVariant || false)

  const handleTintChange = useCallback(
    formattedTints => {
      dispatch(
        updateCartItem({
          updatedFields: { ColorVariants: formattedTints },
          matchCriteria: { ProductId: product.ProductId },
        })
      )
    },
    [dispatch, product.ProductId]
  )

  const {
    tints,
    selectedTints,
    isLoadingTints,
    isErrorTints,
    toggleTint,
    isSelected,
    shouldShow,
  } = useTintsController({
    sku: product.Sku,
    hasVariant,
    controlledTints: product.ColorVariants ?? [],
    syncKey: product.ProductId,
    onChange: handleTintChange,
  })

  const { data: detailedProduct } = useProductDetails(
    { productId: product.ProductId, companyId, warehouse },
    !isWarrantyItem
  )

  // WARRANTY
  const { addWarranty } = useHandleWarranty({
    updateIfExists: true,
  })
  const { data: warrantyOptions = [] } = useWarrantyTypes()

  const selectedWarranty = useMemo(() => {
    if (!product.WarrantyYears) return noWarranty
    return (
      warrantyOptions.find(o => o.WarrantyYears === product.WarrantyYears) ||
      noWarranty
    )
  }, [product.WarrantyYears, warrantyOptions])

  const handleWarrantyChange = useCallback(
    option => {
      dispatch(
        updateCartItem({
          matchCriteria: {
            LineNum: product.LineNum,
            IsExtendedWarranty: false,
          },
          updatedFields: {
            WarrantyYears: option.WarrantyYears ?? null,
          },
        })
      )
      addWarranty({
        selectedWarranty: option,
        productId: product.ProductId,
      })
    },
    [addWarranty, product.ProductId, product.LineNum, dispatch]
  )

  const prevPriceRef = useRef(product.Price)
  useEffect(() => {
    const prevPrice = prevPriceRef.current
    prevPriceRef.current = product.Price

    if (!prevPrice || prevPrice === product.Price) return
    if (!selectedWarranty?.WarrantyYears) return
    if (!warrantyCartItem) return

    const warrantyOption = warrantyOptions.find(
      o => o.WarrantyYears === selectedWarranty.WarrantyYears
    )
    if (!warrantyOption?.WarrantyPercent) return

    const newPrice = (product.Price * warrantyOption.WarrantyPercent) / 100
    const qty = warrantyCartItem.Quantity || 1
    const taxRate = warrantyCartItem.TaxCategoryValue || 0
    const newAmount = newPrice * qty
    const newTax = newAmount * taxRate
    dispatch(
      updateCartItem({
        updatedFields: {
          Price: newPrice,
          BasePrice: newPrice,
          FinalPrice: newPrice,
          Amount: newAmount,
          AmountWithTax: newAmount + newTax,
          Tax: newTax,
        },
        matchCriteria: {
          WarrantyProductId: product.ProductId,
          IsExtendedWarranty: true,
        },
      })
    )
  }, [
    product.Price,
    selectedWarranty,
    warrantyOptions,
    warrantyCartItem,
    dispatch,
    product.ProductId,
  ])

  // Modal states
  const [showModalPrecio, setShowModalPrecio] = useState(false)
  const [showModalSuccessUpdatePrecio, setShowModalSuccessUpdatePrecio] =
    useState(false)

  // Memoize only what's needed
  const titleStyle = useMemo(
    () => ({ fontSize: '12px', fontWeight: 'normal' }),
    []
  )

  const shouldShowServiceCard = useMemo(() => {
    return (
      installable === true &&
      relatedServices?.length > 0 &&
      relatedServices[0]?.productId
    )
  }, [installable, relatedServices])

  const handleModalEditPrice = useCallback(() => {
    setShowModalPrecio(prev => !prev)
  }, [])

  const handleModalSuccessUpdatePrecio = useCallback(() => {
    if (showModalSuccessUpdatePrecio === false) {
      setShowModalSuccessUpdatePrecio(true)
      setTimeout(() => setShowModalSuccessUpdatePrecio(false), 5000)
    } else {
      setShowModalSuccessUpdatePrecio(false)
    }
  }, [showModalSuccessUpdatePrecio])

  const handleDeleteFromCart = useCallback(() => {
    onDeleteFromCart(product)
  }, [onDeleteFromCart, product])

  const seeMargenProd = useValidatePermission(VAPermissions.seeMargenProd)
  const canChangePrice = useValidatePermission(VAPermissions.priceOverrideAutho)
  const seeCostProd = useValidatePermission(VAPermissions.seeCostProd)
  const cost = product.Cost
  const avgCost = product.AvgCost
  const quantityProduct = product.Quantity
  const price = product.Price

  const visitDiscountPerUnit = product.VisitDiscountAmount
    ? Math.ceil((product.VisitDiscountAmount / product.Quantity) * 10000) /
      10000
    : 0
  const effectiveUnitPrice = product.Price - visitDiscountPerUnit

  useEffect(() => {
    if (!detailedProduct) return

    const installableValue =
      detailedProduct.PIMAttributes?.attributes?.coreDetail?.installable
    const hasExtendedWarrantyValue =
      detailedProduct.PIMAttributes?.attributes?.coreDetail?.hasExtendedWarranty
    const relatedServicesValue =
      detailedProduct.PIMAttributes?.relatedServices || []
    const hasVariantValue = detailedProduct.HasVariant || false
    const productTypeIdValue = detailedProduct.ProductTypeId

    const existingServices = product.relatedServices ?? []
    const servicesMatch =
      existingServices.length === relatedServicesValue.length &&
      relatedServicesValue.every(
        (svc, i) => svc.productId === existingServices[i]?.productId
      )

    const alreadyEnriched =
      product.installable === installableValue &&
      product.hasExtendedWarranty === hasExtendedWarrantyValue &&
      product.HasVariant === hasVariantValue &&
      product.ProductTypeId === productTypeIdValue &&
      servicesMatch

    if (alreadyEnriched) return

    setInstallable(installableValue)
    setHasExtendedWarranty(hasExtendedWarrantyValue)
    setRelativeServices(relatedServicesValue)
    setHasVariant(hasVariantValue)

    dispatch(
      updateCartItem({
        updatedFields: {
          installable: installableValue,
          hasExtendedWarranty: hasExtendedWarrantyValue,
          relatedServices: relatedServicesValue,
          HasVariant: hasVariantValue,
          ProductTypeId: productTypeIdValue,
        },
        matchCriteria: {
          ProductId: product.ProductId,
        },
      })
    )
  }, [detailedProduct])

  const isProductDummy = isDummyLitaProduct(product)

  return (
    <div
      className="cart-card"
      data-testid={`cart-product-${product?.Sku}`}
      style={
        isProductDummy
          ? {
              backgroundColor: 'var(--error-background-color)',
              border: '2px solid var(--error-color)',
              borderRadius: '8px',
            }
          : undefined
      }>
      <Row className="product-row">
        <Col md={{ span: 3 }}>
          <img
            src={
              (product.Images && product.Images[0].URL) ||
              product.productImage ||
              product.DefaultProductPicture ||
              product.URL ||
              NoImage
            }
            alt="product-cart"
            className="img-fluid shadow-2-strong"
          />
        </Col>
        <Col md={{ span: 9 }}>
          <Row className="product-card-top-content">
            <Col md={{ span: 8 }} className="product-cart-text">
              <div className="product-cart-product-generals">
                <div className="top-text">
                  <p style={{ whiteSpace: 'pre-line' }}>
                    {getProductDisplayText(product)}
                  </p>
                  <p>{product.Sku}</p>
                </div>
              </div>
            </Col>
            <Col md={{ span: 4 }}>
              <div className="remove-total">
                <div
                  className="trash"
                  data-testid={`btn-delete-product-${product?.Sku}`}>
                  <FontAwesomeIcon
                    icon={faTrashCan}
                    onClick={handleDeleteFromCart}
                  />
                </div>
              </div>
            </Col>
          </Row>
          <Row>
            <Col md={{ span: 9 }} sm={{ span: 11 }}>
              <div className="product-cart-product-numbers">
                <div className="product-cart-product-generals-amount">
                  <div className="counter">
                    <Dash
                      data-testid={`btn-decrease-qty-${product?.Sku}`}
                      style={{
                        border: '1px solid #F8F9FD',
                        borderRadius: '4px',
                        cursor: 'pointer',
                      }}
                      onClick={() => {
                        handleRemoveProduct()
                      }}
                    />
                    <InputMask
                      data-testid={`input-product-qty-${product?.Sku}`}
                      mask={
                        allowFractionalQty
                          ? /^\d{1,6}(\.\d{1,4})?$/
                          : /^\d{1,6}?$/
                      }
                      maskChar=""
                      value={quantity}
                      onChange={event => {
                        handleProdQuantity(event)
                      }}
                      onBlur={handleBlur}
                      className="number"
                      inputMode="decimal"
                    />
                    <Plus
                      data-testid={`btn-increase-qty-${product?.Sku}`}
                      style={{
                        border: '1px solid #F8F9FD',
                        borderRadius: '4px',
                        cursor: 'pointer',
                      }}
                      onClick={() => {
                        handleAddProduct()
                      }}
                    />
                  </div>
                  <p className="multiply">X</p>
                  <div className="edit">
                    {product.FinalPrice &&
                      (product?.Price !== product.FinalPrice ||
                        product.VisitDiscountAmount) && (
                        <p
                          style={{
                            textDecoration: 'line-through',
                            color:
                              product?.Price === product.FinalPrice
                                ? undefined
                                : 'orange',
                          }}>
                          {'$' +
                            Formatter.Money(
                              product?.Price === product.FinalPrice
                                ? product.Price
                                : product.FinalPrice,
                              { rounded: true, decimalPrecision: 2 }
                            )}
                        </p>
                      )}
                    {product.VisitDiscountAmount ? (
                      <p>{'$' + Formatter.Money(effectiveUnitPrice)}</p>
                    ) : (
                      product.FinalPrice && (
                        <p>{'$' + Formatter.Money(product.Price)}</p>
                      )
                    )}
                  </div>
                  {!isWarrantyItem && !isCustomerUser && (
                    <FontAwesomeIcon
                      icon={faSquarePen}
                      data-testid={`btn-edit-price-${product?.Sku}`}
                      onClick={handleModalEditPrice}
                      title={t('Edit')}
                      className="square-pen-edit"
                    />
                  )}
                </div>
              </div>
            </Col>
            <Col md={{ span: 3 }} sm={{ span: 1 }}>
              <div className="final-price-cart">
                <p data-testid={`final-price-value-${product?.Sku}`}>
                  {'$' +
                    Formatter.Money(
                      product?.Price * product.Quantity -
                        (product.VisitDiscountAmount ?? 0),
                      { rounded: true, decimalPrecision: 2 }
                    )}
                </p>
              </div>
            </Col>
          </Row>
        </Col>

        <Col md={{ span: 9 }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            {!isWarrantyItem && (seeMargenProd || seeCostProd) ? (
              <CostMarginVisibility
                seeCostProd={seeCostProd}
                seeMargenProd={seeMargenProd}
                cost={cost}
                avgCost={avgCost}
                quantity={quantityProduct}
                price={price}
                isMargin
                isCost
                secondaryColor
              />
            ) : null}
          </div>

          {shouldShow && (
            <TintDropdown
              tints={tints}
              isLoading={isLoadingTints}
              isError={isErrorTints}
              onSelect={toggleTint}
              isSelected={isSelected}
              selectedTints={selectedTints}
              shouldShow={shouldShow}
            />
          )}

          {shouldShowServiceCard && (
            <ServiceCard services={relatedServices} titleStyle={titleStyle} />
          )}

          {hasExtendedWarranty && warrantyOptions.length > 0 && (
            <ExtendedWarrantyCard
              price={product.Price}
              product={product}
              showDescription={false}
              groupName={`warranty-cart-${product.LineNum}`}
              warrantyOptions={warrantyOptions}
              selectedWarranty={selectedWarranty}
              onWarrantySelectChange={handleWarrantyChange}
              styleCard={{ background: '#E9F2FE 0% 0% no-repeat padding-box' }}
            />
          )}
        </Col>
      </Row>

      {/* MODAL UPDATE PRICE */}
      <Modal
        data-testid={`modal-edit-price-${product?.Sku}`}
        container={productsRef.current}
        show={showModalPrecio}
        backdrop="static"
        className="modal-clase-price-change"
        centered>
        <PrecioProducto
          handleModalEditPrice={handleModalEditPrice}
          handleModalSuccesUpdatePrecio={handleModalSuccessUpdatePrecio}
          product={product}
          setProductChangeValidation={setProductChangeValidation}
          setNuevoPrecio={() => null}
          productsRef={productsRef}
        />
      </Modal>

      {/* MODAL SUCCESS UPDATE PRICE */}
      <Modal
        data-testid={`modal-success-price-update-${product?.Sku}`}
        container={productsRef.current}
        show={showModalSuccessUpdatePrecio}
        backdrop="static"
        className="modal-clase-price-change"
        centered>
        <GenericModal
          setIsOpen={handleModalSuccessUpdatePrecio}
          text={
            !canChangePrice
              ? t('GENERIC_MODAL.REQUEST_PENDT_APPROVE')
              : hasPriceUnderCost && isCommercialSeller
              ? t('GENERIC_MODAL.REQUEST_PENDT_APPROVE')
              : t('GENERIC_MODAL.PRICE_SUCCESS_CHANGED')
          }
          mostrarButtons={false}
        />
      </Modal>
    </div>
  )
}

export default React.memo(CartProduct)
